import * as yup from "yup";
import { serializers, deserializers } from "../utils";
import {
  BaseStorageFee,
  BaseStorageFeePallet,
  BaseStorageFeeRange,
  PriceRange,
} from "./BaseStorageFeeSection/interfaces/baseStorageFeeField";
import {
  ReceivingFeeBillingSection,
  ReceivingFeeTimeEditFieldValues,
  ReceivingFeePalletEditFieldValues,
  ReceivingFeeContainerEditFieldValues,
} from "./ReceivingFeeSection/interfaces";

const rangeSerializedSchema = yup
  .object()
  .shape<PriceRange>({
    from: yup.number().positive().required(),
    to: yup.number().defined(),
    price: yup.number().required().transform(serializers.cents),
  })
  .defined();

const rangeDeserializedSchema = yup
  .object()
  .shape<PriceRange>({
    from: yup.number().positive().required(),
    to: yup.number().defined(),
    price: yup.number().required().transform(deserializers.cents),
  })
  .defined();

const priceRangesSerializedSchema = yup
  .object()
  .shape<BaseStorageFeeRange>({
    ranges: yup.array<PriceRange[]>().of(rangeSerializedSchema).defined(),
    type: yup.string().oneOf(["fixed", "range"]).required(),
  })
  .defined();

const priceRangesDeserializedSchema = yup
  .object()
  .shape<BaseStorageFeeRange>({
    ranges: yup.array<PriceRange>().of(rangeDeserializedSchema).defined(),
    type: yup.string().oneOf(["fixed", "range"]).required(),
  })
  .defined();

// Base storage fee
const baseStorageFeeSerializedSchema = yup.object().shape<BaseStorageFee>({
  pallet: yup
    .object()
    .shape<BaseStorageFeePallet>({
      double: priceRangesSerializedSchema,
      standard: priceRangesSerializedSchema,
    })
    .default(undefined),
  box: priceRangesSerializedSchema,
  shelf: priceRangesSerializedSchema,
  bin: priceRangesSerializedSchema,
});

const baseStorageFeeDeserializedSchema = yup.object().shape<BaseStorageFee>({
  pallet: yup
    .object()
    .default(undefined)
    .shape<BaseStorageFeePallet>({
      double: priceRangesDeserializedSchema,
      standard: priceRangesDeserializedSchema,
    })
    .notRequired(),
  box: priceRangesDeserializedSchema,
  shelf: priceRangesDeserializedSchema,
  bin: priceRangesDeserializedSchema,
});

// Receiving fee
const receivingFeeSerializedSchema = yup.object().shape<ReceivingFeeBillingSection>({
  time: yup
    .object()
    .default(undefined)
    .shape<ReceivingFeeTimeEditFieldValues>({
      items: yup
        .array()
        .of(
          yup
            .object()
            .shape({
              type: yup.string().defined(),
              price: yup.number().defined().transform(serializers.cents),
            })
            .defined()
        )
        .defined(),
    }),
  pallet: yup
    .object()
    .default(undefined)
    .shape<ReceivingFeePalletEditFieldValues>({
      items: yup
        .array()
        .of(
          yup
            .object()
            .shape({
              type: yup.string().oneOf(["standard"]).defined(),
              price: yup.number().defined().transform(serializers.cents),
            })
            .defined()
        )
        .defined(),
    }),
  box: yup
    .object()
    .default(undefined)
    .shape<ReceivingFeeContainerEditFieldValues>({
      items: yup
        .array()
        .of(
          yup
            .object()
            .shape({
              type: yup.string().oneOf(["standard"]).defined(),
              price: yup.number().defined().transform(serializers.cents),
            })
            .defined()
        )
        .defined(),
    }),
});

const receivingFeeDeserializedSchema = yup.object().shape<ReceivingFeeBillingSection>({
  time: yup
    .object()
    .default(undefined)
    .shape<ReceivingFeeTimeEditFieldValues>({
      items: yup
        .array()
        .of(
          yup
            .object()
            .shape({
              type: yup.string().defined(),
              price: yup.number().defined().transform(deserializers.cents),
            })
            .defined()
        )
        .defined(),
    }),
  pallet: yup
    .object()
    .default(undefined)
    .shape<ReceivingFeePalletEditFieldValues>({
      items: yup
        .array()
        .of(
          yup
            .object()
            .shape({
              type: yup.string().oneOf(["standard"]).defined(),
              price: yup.number().defined().transform(deserializers.cents),
            })
            .defined()
        )
        .defined(),
    }),
  box: yup
    .object()
    .default(undefined)
    .shape<ReceivingFeeContainerEditFieldValues>({
      items: yup
        .array()
        .of(
          yup
            .object()
            .shape({
              type: yup.string().oneOf(["standard"]).defined(),
              price: yup.number().defined().transform(deserializers.cents),
            })
            .defined()
        )
        .defined(),
    }),
});

export const serializedFormSchema = yup.object().shape({
  baseStorageFee: baseStorageFeeSerializedSchema.default(undefined),
  receivingFee: receivingFeeSerializedSchema.default(undefined),
  specialProjects: yup
    .object()
    .shape({
      price: yup.number().positive().transform(serializers.cents),
      type: yup.string(),
    })
    .default(undefined),
  minStorageFee: yup.number().positive().transform(serializers.cents).default(undefined),
});

export const deserializedFormSchema = yup.object().shape({
  baseStorageFee: baseStorageFeeDeserializedSchema.default(undefined),
  receivingFee: receivingFeeDeserializedSchema.default(undefined),
  specialProjects: yup
    .object()
    .shape({
      price: yup.number().positive().transform(deserializers.cents),
      type: yup.string(),
    })
    .default(undefined),
  minStorageFee: yup.number().positive().transform(deserializers.cents).default(undefined),
});
